import lobEn from '../assets/lob-cards.json'
import lobEs from '../assets/lob-cards-es.json'
import lobPt from '../assets/lob-cards-pt.json'
import { t } from './t'
import { useLocaleStore } from '../stores/locale'

interface EsEntry {
  name: string
  desc: string
}

interface EnCard {
  id: string | number
  name: string
  desc?: string
}

const ES = lobEs as Record<string, EsEntry>
const PT = lobPt as Record<string, EsEntry>
const EN_LIST = (lobEn as { data: EnCard[] }).data
const EN_BY_ID = new Map(EN_LIST.map((card) => [String(card.id), card]))
const EN_NAME_TO_ID = new Map(EN_LIST.map((card) => [card.name, String(card.id)]))

function term(
  kind: 'attr' | 'race' | 'type' | 'token' | 'stType',
  value: string,
): string {
  if (!value) return value
  const key = `card.${kind}.${value}`
  const hit = t(key)
  return hit === key ? value : hit
}

function catalog(): Record<string, EsEntry> | null {
  const locale = useLocaleStore().locale
  if (locale === 'es') return ES
  if (locale === 'pt') return PT
  return null
}

function localizeTypeline(tokens?: string[]): string[] | undefined {
  if (!tokens) return tokens
  return tokens.map((token) => {
    const asToken = term('token', token)
    if (asToken !== token) return asToken
    return term('race', token)
  })
}

/**
 * Spell / Trap subtype for the viewer (Equip Spell, Continuous Trap…).
 * Uses catalog `type` + `race`, not typeline (LOB spells/traps have none).
 */
export function spellTrapKindLabel(type?: string, race?: string): string {
  const blob = (type || '').toLowerCase()
  const isSpell = blob.includes('spell') || blob.includes('magic')
  const isTrap = blob.includes('trap')
  if (!isSpell && !isTrap) return ''
  const raceKey = race || 'Normal'
  const kind = isSpell ? 'Spell' : 'Trap'
  return term('stType', `${raceKey} ${kind}`)
}

/** Official TCG name for the current UI locale. */
export function localizedName(id: string | number, fallback = ''): string {
  const key = String(id)
  if (!key) return fallback
  const nameHit = catalog()?.[key]?.name
  if (nameHit) return nameHit
  // Catalog first: callers sometimes pass the Konami id as `fallback`, which
  // is truthy and would otherwise skip the English name lookup.
  return EN_BY_ID.get(key)?.name || fallback || key
}

/** English name printed on the scan. Foil reprints use this, not the locale. */
export function englishName(id: string | number, fallback = ''): string {
  const key = String(id)
  if (!key) return fallback
  return EN_BY_ID.get(key)?.name || fallback
}

/** Effect / flavour text for the current UI locale. */
export function localizedDesc(id: string | number, fallback = ''): string {
  const key = String(id)
  if (!key) return fallback
  const descHit = catalog()?.[key]?.desc
  if (descHit) return descHit
  return EN_BY_ID.get(key)?.desc || fallback || ''
}

/** Map an English catalog name to the current UI locale. */
export function localizedNameFromEnglish(enName: string): string {
  const id = EN_NAME_TO_ID.get(enName)
  if (!id) return enName
  return localizedName(id, enName)
}

type Localizable = {
  id?: string | number
  name?: string
  desc?: string
  type?: string
  race?: string
  attribute?: string
  typeline?: string[]
}

/**
 * Display copy for a catalog card. Stats and ids stay as they are.
 * Safe to call from Vue (tracks locale) and from Phaser.
 */
export function localizeCard<T extends Localizable>(card: T): T {
  const id = card.id
  if (id === undefined || id === null || id === '') return card
  return {
    ...card,
    name: localizedName(id, card.name ?? ''),
    desc: localizedDesc(id, card.desc ?? ''),
    type: card.type ? term('type', card.type) : card.type,
    race: card.race ? term('race', card.race) : card.race,
    attribute: card.attribute ? term('attr', card.attribute) : card.attribute,
    typeline: localizeTypeline(card.typeline),
  }
}
